/** @format */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Profile } from 'src/app/models/profile';
import { TimeBank, BankType } from 'src/app/models/timeBank';
import {
	getExceptionTypes,
	getPayPeriodEntries,
	getPayPeriods,
	getProfile,
	getSelectedYear,
	getTimeBanks,
	getBankTypes,
	getTimesheets,
} from 'src/app/store';
import {
	ApplicationState,
	CalendarMonthlyTimesheets,
	FilterToggleStates,
	LoadNextYear,
	LoadPreviousYear,
	PayPeriod,
	PayPeriodEntry,
	Timesheet,
	ExceptionType,
} from 'src/app/store/application';
import * as HelperFunctions from 'src/assets/helper-functions/helper-functions';

@Component({
	selector: 'app-year-at-a-glance',
	templateUrl: './year-at-a-glance.component.html',
	styleUrls: ['./year-at-a-glance.component.scss'],
})
export class YearAtAGlanceComponent implements OnInit, OnDestroy {
	timesheets$: Observable<Timesheet[]>;
	timesheets: Timesheet[] = [];

	private ngUnsubscribe: Subject<void> = new Subject();

	allCalendarTimesheets: CalendarMonthlyTimesheets[] = [];

	selectedYear$: Observable<number>;
	selectedYear: number = new Date().getFullYear();

	profile$: Observable<Profile>;
	profile!: Profile;

	exceptionTypes$: Observable<ExceptionType[]>;

	bankTypes$: Observable<BankType[]>;
	timeBanks$: Observable<TimeBank[]>;

	application$!: Observable<ApplicationState>;

	filterToggleStates: FilterToggleStates = {
		payPeriod: true,
		payDay: true,
		statHoliday: true,
		holidayClosure: true,
		cdo: true,
		timesheets: true,
	};

	unAppliedFilterToggleStates: FilterToggleStates = {
		payPeriod: true,
		payDay: true,
		statHoliday: true,
		holidayClosure: true,
		cdo: true,
		timesheets: true,
	};

	yearsDataProvided: number[] = [];
	payDates: Date[] = [];
	filterOpen = false;
	payPeriods$: Observable<PayPeriod[]>;
	currPayPeriod: PayPeriod | undefined;
	lastPayPeriod: PayPeriod | undefined;

	constructor(private store: Store<{}>, private offcanvasService: NgbOffcanvas, private router: Router) {
		this.selectedYear$ = this.store.select(getSelectedYear);
		this.profile$ = this.store.select(getProfile);
		this.bankTypes$ = this.store.select(getBankTypes);
		this.timeBanks$ = this.store.select(getTimeBanks);
		this.payPeriods$ = this.store.select(getPayPeriods);
		this.timesheets$ = this.store.select(getTimesheets);
		this.exceptionTypes$ = this.store.select(getExceptionTypes);
	}

	ngOnInit(): void {
		this.selectedYear$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((selectedYear) => (this.selectedYear = selectedYear));
		this.profile$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((profile) => (this.profile = profile));

		this.timesheets$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((timesheets) => {
			this.timesheets = timesheets;
			this.allCalendarTimesheets = [];

			timesheets.forEach((timesheet) => HelperFunctions.getAllTimesheetYearsAndPaydates(timesheet, this.yearsDataProvided, this.payDates));
			HelperFunctions.convertTimesheetToCalendarFriendlyObj(timesheets, this.yearsDataProvided, this.allCalendarTimesheets);
		});

		this.payPeriods$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((pp) => {
			const today = new Date().setHours(0, 0, 0, 0);
			const currPayPeriodArr: PayPeriod[] = [];
			const lastPayPeriodArr: PayPeriod[] = [];
			pp.filter((p, index) => {
				const startDate = new Date(p.startDate).setHours(0, 0, 0, 0);
				const endDate = new Date(p.endDate).setHours(0, 0, 0, 0);
				if (startDate <= today && today <= endDate) {
					currPayPeriodArr.push(p);
					lastPayPeriodArr.push(pp[index > 0 ? index - 1 : 0]);
				}
			});
			this.currPayPeriod = [...currPayPeriodArr].pop();
			this.lastPayPeriod = [...lastPayPeriodArr].pop();
		});
	}

	ngOnDestroy() {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	hasNextYear() {
		return this.yearsDataProvided.includes(this.selectedYear + 1);
	}

	goToNextYear() {
		if (this.hasNextYear()) {
			this.store.dispatch(LoadNextYear());
		}
	}

	hasPreviousYear() {
		return this.yearsDataProvided.includes(this.selectedYear - 1);
	}

	goToPreviousYear() {
		if (this.hasPreviousYear()) {
			this.store.dispatch(LoadPreviousYear());
		}
	}

	extractMonthlyData(currentMonth: number): CalendarMonthlyTimesheets {
		return (
			this.allCalendarTimesheets.find(
				(calendarTimesheets) => calendarTimesheets.year === this.selectedYear && calendarTimesheets.month === currentMonth
			) || {
				year: this.selectedYear,
				month: new Date().getMonth(),
				timesheets: [],
				payDates: [],
			}
		);
	}

	handleRedirectToTimesheet(timesheetURL: string) {
		this.router.navigateByUrl(timesheetURL);
	}

	applyFilterPanelChanges() {
		this.filterToggleStates = { ...this.unAppliedFilterToggleStates };
		this.offcanvasService.dismiss();
	}

	open(content: any) {
		this.unAppliedFilterToggleStates = { ...this.filterToggleStates };
		this.offcanvasService.open(content, { ariaLabelledBy: 'offcanvas-basic-title', position: 'end' });
	}
}
